import React, { useEffect, useState, useCallback } from 'react';
import { MailCheck, AlertTriangle, ChevronRight, Clock } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Link } from 'react-router-dom';
import { getLeaveApprovalsSummary } from '../../services/api';

const fmt = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  } catch { return d; }
};

const LeaveApprovalsCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await getLeaveApprovalsSummary();
      setData(r.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 120_000);
    return () => clearInterval(t);
  }, [load]);

  if (loading || !data || data.pending === 0) return null;

  const hasOverdue = data.overdue > 0;
  // Full static class strings (Tailwind JIT-safe — no dynamic interpolation)
  const c = hasOverdue
    ? {
        card: 'border-rose-300 bg-gradient-to-r from-rose-50 to-orange-50 shadow-md',
        iconWrap: 'bg-rose-100', icon: 'text-rose-700',
        title: 'text-rose-900', sub: 'text-rose-700', badge: 'bg-rose-600',
        btn: 'border-rose-300 text-rose-700 hover:bg-rose-100',
      }
    : {
        card: 'border-indigo-300 bg-gradient-to-r from-indigo-50 to-slate-50 shadow-md',
        iconWrap: 'bg-indigo-100', icon: 'text-indigo-700',
        title: 'text-indigo-900', sub: 'text-indigo-700', badge: 'bg-indigo-600',
        btn: 'border-indigo-300 text-indigo-700 hover:bg-indigo-100',
      };

  return (
    <Card className={c.card} data-testid="leave-approvals-dashboard-card">
      <CardContent className="p-4">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="flex items-center gap-2">
            <div className={`${c.iconWrap} p-2 rounded-lg`}>
              {hasOverdue
                ? <AlertTriangle className={`h-5 w-5 ${c.icon}`} />
                : <MailCheck className={`h-5 w-5 ${c.icon}`} />}
            </div>
            <div>
              <h3 className={`font-bold ${c.title} flex items-center gap-2`}>
                Leave Approvals Awaiting Decision
                <span
                  className={`inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full text-xs font-bold text-white ${c.badge}`}
                  data-testid="leave-approvals-badge"
                >
                  {data.pending}
                </span>
              </h3>
              <p className={`text-xs ${c.sub}`} data-testid="leave-approvals-subtitle">
                {hasOverdue
                  ? `${data.overdue} overdue (pending ≥ ${data.overdue_days} day${data.overdue_days === 1 ? '' : 's'}) · oldest ${data.oldest_age_days}d`
                  : `${data.pending} request${data.pending === 1 ? '' : 's'} sent to managers, awaiting reply`}
              </p>
            </div>
          </div>
          <Link to="/leaves">
            <Button size="sm" variant="outline" className={c.btn} data-testid="leave-approvals-review-btn">
              Review <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        </div>

        <div className="grid sm:grid-cols-2 gap-2">
          {data.items.map((it) => (
            <div
              key={it.id}
              className={`bg-white rounded-lg p-2.5 border flex items-center justify-between gap-2 ${it.overdue ? 'border-rose-200' : 'border-slate-200'}`}
              data-testid={`approval-summary-row-${it.id}`}
            >
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-sm text-gray-900 truncate">
                  {it.employee_name}
                  <span className="text-[10px] font-mono text-gray-400 ml-1">· {it.employee_code}</span>
                </p>
                <p className="text-[11px] text-gray-500 truncate">
                  {it.leave_type_label} · {fmt(it.start_date)} → {fmt(it.end_date)} ({it.days}d)
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className={`text-xs font-semibold flex items-center gap-1 justify-end ${it.overdue ? 'text-rose-700' : 'text-slate-500'}`}>
                  <Clock className="h-3 w-3" /> {it.age_days}d
                </p>
                {it.reminders_sent > 0 && (
                  <p className="text-[10px] text-amber-600">{it.reminders_sent} reminder{it.reminders_sent === 1 ? '' : 's'}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default LeaveApprovalsCard;
